import { darken, lighten, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { GridColDef } from "@mui/x-data-grid";
import { MaterialRequirementStatusCell } from "components/atoms";
import { AppTable } from "components/molecules";
import { TableHelper, TimeHelper } from "helpers";
import { usePaginationRequest } from "hooks";
import { orderBy } from "lodash";
import {
  IGetMaterialRequirementDetailRequest,
  IGetMaterialRequirementSuggestPalletsRequest,
  IMaterialRequirementDetail,
} from "model";
import { useState } from "react";
import { MaterialRequirementApiService } from "services";
import { theme } from "themes";
import { CURRENT_PAGE_SIZE, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";

const getBackgroundColor = (color: string) => {
  return "#C6E4FF";
};
const getRowClassName = (row: any) => {
  const name = !(
    row.row.requirementQuantity <= row.row.stockerRequirementQuantity
  )
    ? "true"
    : "false";
  return `super-app-theme--${name}`;
};
interface IProductListProps {
  id: string;
}
const MaterialList = (props: IProductListProps) => {
  const { id } = props;
  const [refresh, setRefresh] = useState(false);

  const [request, setRequest] = useState<IGetMaterialRequirementDetailRequest>({
    requestMaterialId: id,
    documentNbr: "",
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
  });

  const { loading, result } = usePaginationRequest<
    IGetMaterialRequirementSuggestPalletsRequest,
    IMaterialRequirementDetail[]
  >(
    request,
    MaterialRequirementApiService.getMaterialRequirementDetailById,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );

  const _handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber,
    });
  };

  const _handleSearch = (searchText: string) => {
    setRequest({
      ...request,
      searchText,
    });
  };

  const _handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.receive.productCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productName",
      headerName: I18nTranslator(translations.receive.productName),
      size: "large",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productLot",
      headerName: I18nTranslator(translations.receive.productLot),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "manufacturingDate",
      headerName: I18nTranslator(translations.receive.manufacturingDate),
      size: "small",
      align: "left",
      renderCell: ({ value }) => {
        return (
          <Typography>
            {value ? TimeHelper.formatDate(value, "DD/MM/YYYY") : ""}
          </Typography>
        );
      },
    }),
    TableHelper.createCell({
      field: "expiryDate",
      headerName: I18nTranslator(translations.receive.expiredDate),
      size: "small",
      align: "left",
      renderCell: ({ value }) => {
        return (
          <Typography>
            {value ? TimeHelper.formatDate(value, "DD/MM/YYYY") : ""}
          </Typography>
        );
      },
    }),
    TableHelper.createCell({
      field: "status",
      headerName: I18nTranslator(translations.receive.status),
      size: "small",
      renderCell: ({ row: { status } }) => (
        <MaterialRequirementStatusCell value={status} />
      ),
    }),
    TableHelper.createCell({
      field: "requestedQty",
      headerName: I18nTranslator(translations.receive.requirementQuantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "bookedQty",
      headerName: I18nTranslator(
        translations.receive.stockerRequirementQuantity
      ),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "preparedQty",
      headerName: I18nTranslator(translations.receive.preparedQuantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "deliveredQty",
      headerName: I18nTranslator(translations.receive.deliveredQuantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "note",
      headerName: I18nTranslator(translations.receive.description),
      size: "full",
    }),
  ];
  return (
    <AppTable<any>
      loading={loading}
      data={{
        ...result,
        data: orderBy(result.data, ["stockerRequirementQuantity"], "desc"),
      }}
      columns={COLUMNS}
      hideAddNew
      onSearch={_handleSearch}
      searchPlaceholder={I18nTranslator(
        translations.placeholder.searchLoadingOrderPallet
      )}
      onChangePage={_handleChangePage}
      removeActionColumn
      hideHeader
      defaultHeaderTable={330}
      onPageSizeChange={_handlePageSizeChange}
      getRowClassName={getRowClassName}
    />
  );
};

export default MaterialList;
